// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { FEEDBACK_CAPS, type FeedbackCategory } from '@greenlight/shared';

const CATEGORY_OPTIONS: ReadonlyArray<{ value: FeedbackCategory; label: string }> = [
  { value: 'bug', label: 'Bug' },
  { value: 'friction', label: 'Friction' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'other', label: 'Other' },
];

export interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The shell-level "Send feedback" dialog (docs/28 §/feedback). Available to every
 * role on every page; the form captures a short report and auto-captures the
 * current route + client context so a UX bug says where it happened without the
 * user typing it. The server derives source='user' / surface='dashboard' — the
 * client never claims them. Bodies are untrusted downstream, so they are only
 * ever rendered inert on the triage surface.
 */
export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps): React.JSX.Element {
  const location = useLocation();
  const submit = api.feedback.submit.useMutation();
  const [category, setCategory] = React.useState<FeedbackCategory>('bug');
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const t = title.trim();
    const d = description.trim();
    if (!t || !d) {
      setError('A title and description are both required.');
      return;
    }
    try {
      const res = await submit.mutateAsync({
        body: {
          category,
          title: t,
          body: d,
          context: {
            route: `${location.pathname}${location.search}`,
            user_agent: navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
          },
        },
      });
      if (res.status >= 300) throw res;
      toast.success('Thanks — your feedback was sent to the platform team.');
      onOpenChange(false);
    } catch {
      setError('Could not send feedback. Please try again.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Report a bug or share an idea about the Greenlight dashboard. We capture the current
            page automatically.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="feedback-category">Category</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as FeedbackCategory)}>
              <SelectTrigger id="feedback-category" data-testid="feedback-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="feedback-title">Title</Label>
            <Input
              id="feedback-title"
              value={title}
              maxLength={FEEDBACK_CAPS.titleMaxChars}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="One-line summary"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="feedback-description">Description</Label>
            <Textarea
              id="feedback-description"
              value={description}
              maxLength={FEEDBACK_CAPS.bodyMaxChars}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened, and what did you expect?"
              rows={5}
            />
          </div>
          {error && <p className="text-[11.5px] text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" data-testid="feedback-submit" disabled={submit.isPending}>
              {submit.isPending ? 'Sending…' : 'Send feedback'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
